/// <reference types="geojson" />
// MapLibre wrapper: sources/layers are created once; every state change after that only calls
// `setFeatureState` (per-PLZ colour value) and `setPaintProperty` (colour stops) — no source or
// map recreation, per docs/DESIGN.md §6.3 and the WP4 performance requirement.
import maplibregl from "maplibre-gl";
import type { OutlineFeatureCollection, PlzFeatureCollection, PlzIndex } from "../registry";

export type PlzSourceConfig =
  | { kind: "geojson"; data: PlzFeatureCollection }
  | { kind: "vector"; url: string; sourceLayer: string };

export interface MapCallbacks {
  onHover(plz: string | null, point: { x: number; y: number }): void;
  onClick(plz: string): void;
  onContextAction(plz: string, point: { x: number; y: number }): void;
}

const NO_DATA_COLOR = "#cccccc";
const LONG_PRESS_MS = 500;

// "Reachable within" dimming (docs/design-isochrones.md §3.3): a second feature-state field,
// `reach` (0|1), drives fill/line opacity via a static expression — never a paint-property swap.
// Feature-state left unset (no reach filter loaded, or the filter is "Any") is neither `0` nor
// `1`, so the `==` comparisons below fall through to full opacity by construction.
const reachOpacity = (dimmed: number, full: number) =>
  ["case", ["==", ["feature-state", "reach"], 0], dimmed, full] as unknown as maplibregl.DataDrivenPropertyValueSpecification<number>;
const REACH_FILL_OPACITY = reachOpacity(0.22, 0.92);
const REACH_LINE_OPACITY = reachOpacity(0.15, 0.5);

export class EscapeMapView {
  readonly map: maplibregl.Map;
  private readonly plzSourceId = "plz";
  private readonly sourceLayer: string | undefined;
  private ready: Promise<void>;

  constructor(
    container: HTMLElement,
    plzSource: PlzSourceConfig,
    outline: OutlineFeatureCollection,
    callbacks: MapCallbacks,
  ) {
    this.sourceLayer = plzSource.kind === "vector" ? plzSource.sourceLayer : undefined;

    const sources: Record<string, maplibregl.SourceSpecification> =
      plzSource.kind === "geojson"
        ? {
            [this.plzSourceId]: {
              type: "geojson",
              data: plzSource.data as unknown as GeoJSON.FeatureCollection,
              promoteId: "plz",
            },
          }
        : {
            [this.plzSourceId]: {
              type: "vector",
              url: plzSource.url,
              promoteId: { [plzSource.sourceLayer]: "plz" },
            },
          };
    sources.outline = { type: "geojson", data: outline as unknown as GeoJSON.FeatureCollection };
    sources["ref-point"] = { type: "geojson", data: { type: "FeatureCollection", features: [] } };

    const sl = this.sourceLayer ? { "source-layer": this.sourceLayer } : {};

    this.map = new maplibregl.Map({
      container,
      style: {
        version: 8,
        sources,
        layers: [
          { id: "bg", type: "background", paint: { "background-color": "#e7ebe8" } },
          {
            id: "plz-fill",
            type: "fill",
            source: this.plzSourceId,
            ...sl,
            paint: { "fill-color": NO_DATA_COLOR, "fill-opacity": REACH_FILL_OPACITY },
          },
          {
            id: "plz-line",
            type: "line",
            source: this.plzSourceId,
            ...sl,
            paint: { "line-color": "#8a938f", "line-width": 0.3, "line-opacity": REACH_LINE_OPACITY },
          },
          { id: "outline-line", type: "line", source: "outline", paint: { "line-color": "#5f6b66", "line-width": 0.8, "line-opacity": 0.6 } },
          { id: "plz-ref-line", type: "line", source: this.plzSourceId, ...sl, filter: ["==", ["get", "plz"], "__none__"], paint: { "line-color": "#1b2628", "line-width": 2.5 } },
          { id: "ref-marker", type: "circle", source: "ref-point", paint: { "circle-radius": 6, "circle-color": "#1b2628", "circle-stroke-color": "#ffffff", "circle-stroke-width": 2 } },
        ],
      },
      center: [8.2, 46.8],
      zoom: 7,
      minZoom: 6,
      maxZoom: 12,
      // The legend (ui/legend.ts) already carries the required attribution line; MapLibre's own
      // attribution control would duplicate it and, at 400px width, overlap the bottom sheet's
      // Reference PLZ field.
      attributionControl: false,
    });

    this.ready = new Promise((resolve) => this.map.on("load", () => resolve()));

    this.map.on("mousemove", "plz-fill", (e) => {
      const f = e.features?.[0];
      const plz = (f?.properties as { plz?: string } | undefined)?.plz ?? null;
      callbacks.onHover(plz, { x: e.point.x, y: e.point.y });
    });
    this.map.on("mouseleave", "plz-fill", () => callbacks.onHover(null, { x: 0, y: 0 }));
    this.map.on("click", "plz-fill", (e) => {
      const plz = (e.features?.[0]?.properties as { plz?: string } | undefined)?.plz;
      if (plz) callbacks.onClick(plz);
    });
    this.map.on("contextmenu", "plz-fill", (e) => {
      e.preventDefault();
      const plz = (e.features?.[0]?.properties as { plz?: string } | undefined)?.plz;
      if (plz) callbacks.onContextAction(plz, { x: e.point.x, y: e.point.y });
    });

    // Long-press (>=500ms) is the touch equivalent of right-click for "set as reference".
    let pressTimer: ReturnType<typeof setTimeout> | undefined;
    const cancelPress = () => {
      if (pressTimer !== undefined) clearTimeout(pressTimer);
      pressTimer = undefined;
    };
    this.map.on("touchstart", "plz-fill", (e) => {
      const plz = (e.features?.[0]?.properties as { plz?: string } | undefined)?.plz;
      const point = { x: e.point.x, y: e.point.y };
      cancelPress();
      if (plz) pressTimer = setTimeout(() => callbacks.onContextAction(plz, point), LONG_PRESS_MS);
    });
    this.map.on("touchend", cancelPress);
    this.map.on("touchmove", cancelPress);
    this.map.on("touchcancel", cancelPress);
  }

  whenReady(): Promise<void> {
    return this.ready;
  }

  /** Colour stops for the choropleth: `["case", missing?, grey, ["interpolate", ...]]`. */
  setFillExpression(expression: unknown): void {
    this.map.setPaintProperty("plz-fill", "fill-color", expression as maplibregl.ExpressionSpecification);
  }

  /**
   * Push a `v` (or null) feature-state for every PLZ in one synchronous loop — the only
   * per-frame work; geometry and sources are never touched again after construction.
   */
  setValues(values: Map<string, number | null>): void {
    // `id` must match the type promoteId produces at runtime, which is the raw "plz" property
    // value — a string (per the data contract, plz is a zero-padded 4-digit string). Passing a
    // number here is the classic feature-id mismatch that silently breaks hover/colour (see
    // docs/PLAN.md "Feature-id plumbing in WP4").
    for (const [plz, v] of values) {
      this.map.setFeatureState({ source: this.plzSourceId, sourceLayer: this.sourceLayer, id: plz }, { v });
    }
  }

  /**
   * Push a `reach` (0|1) feature-state for every PLZ in one synchronous loop, same shape as
   * `setValues`. `1` (or unset) paints at full opacity; `0` dims per REACH_FILL/LINE_OPACITY.
   * Independent of `setValues`'s `v` field — MapLibre's setFeatureState merges, it never
   * replaces the whole state object, so the two can be called in either order.
   */
  setReach(reach: Map<string, 0 | 1>): void {
    for (const [plz, r] of reach) {
      this.map.setFeatureState({ source: this.plzSourceId, sourceLayer: this.sourceLayer, id: plz }, { reach: r });
    }
  }

  setReference(plzId: string, index: PlzIndex): void {
    this.map.setFilter("plz-ref-line", ["==", ["get", "plz"], plzId]);
    const record = index[plzId];
    const source = this.map.getSource("ref-point") as maplibregl.GeoJSONSource | undefined;
    if (source && record) {
      source.setData({
        type: "FeatureCollection",
        features: [{ type: "Feature", properties: {}, geometry: { type: "Point", coordinates: [record.ref_lon, record.ref_lat] } }],
      });
    }
  }
}
